import { appHeader, appType, getRedirectUrl } from 'tlv-auth/src/comp/AppType'
import { getSystemCulture } from '../../utils/systemCulture.js'

const ENV_PREFIX =
  import.meta.env.VITE_APP_STORAGE_NAMESPACE || (import.meta.env.PROD ? 'PROD' : 'DEV')
const AUTH_FLOW_ID_STORAGE_KEY = `FINDEE_AUTH_${ENV_PREFIX}_FLOW_ID`
const AUTH_DEBUG_EVENTS_STORAGE_KEY = `FINDEE_AUTH_${ENV_PREFIX}_DEBUG_EVENTS`
const MAX_STORED_DEBUG_EVENTS = 50
const MAX_HEADER_VALUE_LENGTH = 1024
const MAX_LOG_STRING_LENGTH = 512
const REDACTED_VALUE = '[redacted]'
const SENSITIVE_LOG_KEYS = new Set([
  'accesstoken',
  'authorizationcode',
  'code',
  'credential',
  'email',
  'idtoken',
  'password',
  'refreshtoken',
  'secret',
  'token'
])
const SENSITIVE_QUERY_KEYS = new Set([
  'access_token',
  'authorization_code',
  'code',
  'email',
  'id_token',
  'password',
  'refresh_token',
  'token'
])
const SENSITIVE_PATH_SEGMENTS = new Set([
  'confirm-email',
  'password-reset',
  'refresh',
  'reset-verify'
])

const getWindow = () => (typeof window !== 'undefined' ? window : null)

const getSessionStorage = () => {
  try {
    return getWindow()?.sessionStorage ?? null
  } catch {
    return null
  }
}

const createId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  return `auth-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const normalizeKey = (key = '') =>
  String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const sanitizeHeaderValue = (value, maxLength = MAX_HEADER_VALUE_LENGTH) => {
  if (value === null || value === undefined) return ''

  return String(value)
    .replace(/[\r\n]+/g, ' ')
    .trim()
    .slice(0, maxLength)
}

const compactObject = (object) =>
  Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined
    )
  )

const sanitizePathname = (pathname = '') =>
  pathname
    .split('/')
    .map((segment, index, segments) => {
      if (!segment) return segment
      return SENSITIVE_PATH_SEGMENTS.has(segments[index - 1]) ? REDACTED_VALUE : segment
    })
    .join('/')

const getBaseOrigin = () => getWindow()?.location?.origin || 'http://localhost'

export const sanitizeUrlForLog = (url = '') => {
  if (!url) return {}

  try {
    const parsedUrl = new URL(url, getBaseOrigin())
    const searchKeys = [...new Set(parsedUrl.searchParams.keys())].sort()
    const safeSearchKeys = searchKeys.map((key) =>
      SENSITIVE_QUERY_KEYS.has(String(key).toLowerCase()) ? REDACTED_VALUE : key
    )

    return compactObject({
      origin: sanitizeHeaderValue(parsedUrl.origin, 256),
      pathname: sanitizeHeaderValue(sanitizePathname(parsedUrl.pathname), 256),
      queryKeys: safeSearchKeys.join(',')
    })
  } catch {
    return {
      raw: sanitizeHeaderValue(url, 256)
    }
  }
}

const getNavigatorValue = (getter) => {
  try {
    const navigatorValue = typeof navigator !== 'undefined' ? getter(navigator) : ''
    return sanitizeHeaderValue(navigatorValue)
  } catch {
    return ''
  }
}

const getTimeZoneName = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

const getCurrentPageContext = () => sanitizeUrlForLog(getWindow()?.location?.href || '')

const getReferrerContext = () => {
  try {
    return sanitizeUrlForLog(document?.referrer || '')
  } catch {
    return {}
  }
}

const getScreenSummary = () => {
  try {
    const currentWindow = getWindow()
    if (!currentWindow?.screen) return ''

    return sanitizeHeaderValue(
      `${currentWindow.screen.width}x${currentWindow.screen.height}@${currentWindow.devicePixelRatio || 1}`
    )
  } catch {
    return ''
  }
}

const getViewportSummary = () => {
  try {
    const currentWindow = getWindow()
    if (!currentWindow) return ''

    return sanitizeHeaderValue(`${currentWindow.innerWidth}x${currentWindow.innerHeight}`)
  } catch {
    return ''
  }
}

const buildClientContext = () => {
  const pageContext = getCurrentPageContext()
  const referrerContext = getReferrerContext()
  const redirectContext = sanitizeUrlForLog(getRedirectUrl())

  return compactObject({
    mode: import.meta.env.MODE || '',
    appType: appType.value,
    appCode: appHeader.value,
    pageOrigin: pageContext.origin || '',
    pagePathname: pageContext.pathname || '',
    pageQueryKeys: pageContext.queryKeys || '',
    referrerOrigin: referrerContext.origin || '',
    referrerPathname: referrerContext.pathname || '',
    referrerQueryKeys: referrerContext.queryKeys || '',
    redirectOrigin: redirectContext.origin || '',
    redirectPathname: redirectContext.pathname || redirectContext.raw || '',
    language: getNavigatorValue((currentNavigator) => currentNavigator.language),
    languages: getNavigatorValue((currentNavigator) => currentNavigator.languages?.join(',')),
    platform: getNavigatorValue(
      (currentNavigator) => currentNavigator.userAgentData?.platform || currentNavigator.platform
    ),
    online: getNavigatorValue((currentNavigator) => currentNavigator.onLine),
    cookieEnabled: getNavigatorValue((currentNavigator) => currentNavigator.cookieEnabled),
    maxTouchPoints: getNavigatorValue((currentNavigator) => currentNavigator.maxTouchPoints),
    screen: getScreenSummary(),
    viewport: getViewportSummary(),
    timezoneName: getTimeZoneName()
  })
}

const buildContextHeaderValue = () => sanitizeHeaderValue(JSON.stringify(buildClientContext()))

export const getAuthFlowId = () => {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) return createId()

  const cachedFlowId = sanitizeHeaderValue(sessionStorage.getItem(AUTH_FLOW_ID_STORAGE_KEY), 128)
  if (cachedFlowId) return cachedFlowId

  const nextFlowId = createId()
  sessionStorage.setItem(AUTH_FLOW_ID_STORAGE_KEY, nextFlowId)
  return nextFlowId
}

export const createAuthRequestId = () => createId()

export const buildAuthRequestHeaders = ({ requestId }) =>
  compactObject({
    'x-correlation-id': requestId,
    'x-auth-flow-id': getAuthFlowId(),
    'x-client-user-agent': getNavigatorValue((currentNavigator) => currentNavigator.userAgent),
    'x-client-platform': getNavigatorValue(
      (currentNavigator) => currentNavigator.userAgentData?.platform || currentNavigator.platform
    ),
    'x-client-language': getNavigatorValue((currentNavigator) => currentNavigator.language),
    'x-client-timezone-name': getTimeZoneName(),
    'x-client-page': getCurrentPageContext().pathname || '',
    'x-client-referrer':
      [getReferrerContext().origin, getReferrerContext().pathname].filter(Boolean).join('') || '',
    'x-client-auth-context': buildContextHeaderValue(),
    'X-Timezone': sanitizeHeaderValue(new Date().getTimezoneOffset()),
    'X-Culture': getSystemCulture()
  })

const sanitizeLogValue = (key, value) => {
  const normalizedKey = normalizeKey(key)

  if (SENSITIVE_LOG_KEYS.has(normalizedKey)) {
    return REDACTED_VALUE
  }

  if (value instanceof Error) {
    return compactObject({
      name: sanitizeHeaderValue(value.name, 64),
      message: sanitizeHeaderValue(value.message, MAX_LOG_STRING_LENGTH)
    })
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeLogValue(key, item))
  }

  if (value && typeof value === 'object') {
    return compactObject(
      Object.fromEntries(
        Object.entries(value).map(([nestedKey, nestedValue]) => [
          nestedKey,
          sanitizeLogValue(nestedKey, nestedValue)
        ])
      )
    )
  }

  if (
    typeof value === 'string' &&
    (normalizedKey.includes('url') ||
      normalizedKey.includes('target') ||
      normalizedKey.includes('referrer') ||
      normalizedKey.includes('location'))
  ) {
    return sanitizeUrlForLog(value)
  }

  return typeof value === 'string' ? sanitizeHeaderValue(value, MAX_LOG_STRING_LENGTH) : value
}

const persistDebugEvent = (entry) => {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) return

  try {
    const currentValue = sessionStorage.getItem(AUTH_DEBUG_EVENTS_STORAGE_KEY)
    const currentEntries = currentValue ? JSON.parse(currentValue) : []
    const nextEntries = [...currentEntries, entry].slice(-MAX_STORED_DEBUG_EVENTS)

    sessionStorage.setItem(AUTH_DEBUG_EVENTS_STORAGE_KEY, JSON.stringify(nextEntries))
  } catch {
    // Ignore session storage write failures.
  }
}

export const summarizeErrorForLog = (error) => sanitizeLogValue('error', error)

export const logAuthEvent = (eventName, metadata = {}, level = 'info') => {
  const pageContext = getCurrentPageContext()
  const entry = compactObject({
    timestamp: new Date().toISOString(),
    event: eventName,
    flowId: getAuthFlowId(),
    appType: appType.value,
    appCode: appHeader.value,
    page: pageContext.pathname || '',
    pageQueryKeys: pageContext.queryKeys || '',
    ...sanitizeLogValue('metadata', metadata)
  })

  persistDebugEvent(entry)

  const consoleMethod =
    level === 'debug' || level === 'info' || level === 'warn' || level === 'error' ? level : 'info'

  if (typeof console?.[consoleMethod] === 'function') {
    console[consoleMethod](`[AUTH] ${eventName}`, entry)
  }
}
